export type PlaybackSpeed = 'slow' | 'normal' | 'fast';

export type ExportState = 'idle' | 'working' | 'ready' | 'failed';

export interface Project {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  frameIds: string[];
  speed: PlaybackSpeed;
  exportState: ExportState;
}

export interface MovieFrame {
  id: string;
  projectId: string;
  createdAt: number;
  blob: Blob;
  width: number;
  height: number;
  /** The untouched photo, kept when the Magic Eraser replaces `blob`. Lets kids bring back the real picture. */
  originalBlob?: Blob;
}

export interface DeletedFrame {
  frame: MovieFrame;
  index: number;
}

export interface ProjectFrames {
  frames: MovieFrame[];
  missingCount: number;
}

export const SPEEDS: Readonly<Record<PlaybackSpeed, { label: string; fps: number; hint: string }>> = {
  slow: { label: 'Slow', fps: 4, hint: '4 pictures each second' },
  normal: { label: 'Normal', fps: 8, hint: '8 pictures each second' },
  fast: { label: 'Fast', fps: 12, hint: '12 pictures each second' },
};

export const MIN_EXPORT_FRAMES = 2;
export const MAX_CAPTURE_EDGE = 1280;

/** A finished, exported movie kept in the local library. Frozen snapshot: editing the project later never changes it. */
export interface SavedMovie {
  id: string;
  /** Null once the source project is deleted; the movie stays. */
  projectId: string | null;
  title: string;
  createdAt: number;
  mimeType: string;
  extension: 'mp4' | 'webm';
  durationMs: number;
  frameCount: number;
  speed: PlaybackSpeed;
  width: number;
  height: number;
  sizeBytes: number;
  /** Small poster image (first frame). */
  posterBlob?: Blob;
  /** Web tier: the video bytes live in IndexedDB. */
  blob?: Blob;
  /** Native tier: the video lives as a real file; this is its URI. */
  fileUri?: string;
  /** Soft-delete tombstone; purged on next visit to the library. */
  deletedAt?: number;
}

/** Web-tier cap for a single saved movie (iOS WebKit large-blob risk). */
export const MAX_SAVED_MOVIE_BYTES = 150 * 1024 * 1024;
/** Extra headroom demanded beyond the movie size (service-worker cache shares the quota). */
export const SAVE_HEADROOM_RATIO = 1.2;

export function savedMovieTitle(project: Project): string {
  const date = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${safeMovieTitle(project.title, project.createdAt)} — ${date}`;
}

export function formatMovieSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function formatMovieDuration(durationMs: number): string {
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export function makeId(prefix: 'movie' | 'frame' | 'saved'): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function safeMovieTitle(title: string, createdAt: number): string {
  const forbidden = '<>:"/\\|?*';
  const cleaned = [...title]
    .filter((character) => character.charCodeAt(0) >= 32 && !forbidden.includes(character))
    .join('')
    .trim()
    .slice(0, 60);
  if (cleaned) return cleaned;
  const date = new Date(createdAt);
  return `My Movie ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export function movieFilename(project: Project, extension: 'mp4' | 'webm'): string {
  const base = safeMovieTitle(project.title, project.createdAt)
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .slice(0, 48) || 'my-movie';
  return `${base}.${extension}`;
}
